// src/paginas/SolicitarAcceso.js
// Formulario público para solicitar el alta de una cuenta (Administrador o Técnico).
// La cuenta queda en estado "pendiente" hasta que el Super Administrador la aprueba
// desde el correo que recibe automáticamente (ver api/aprobar_usuario.php).
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../servicios/api';

const SolicitarAcceso = () => {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [rol, setRol] = useState('tecnico');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('');

  const manejarEnvio = async (e) => {
    e.preventDefault();
    setMensaje('');
    setEnviando(true);

    try {
      const response = await fetch(`${API_URL}/registro.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, email, password, rol }),
      });

      const datos = await response.json();
      setTipoMensaje(datos.exito ? 'exito' : 'error');
      setMensaje(datos.mensaje);

      if (datos.exito) {
        setNombre('');
        setEmail('');
        setPassword('');
        setRol('tecnico');
      }
    } catch (error) {
      console.error('Error al solicitar acceso:', error);
      setTipoMensaje('error');
      setMensaje('Error de conexión con el servidor. Inténtalo de nuevo más tarde.');
    } finally {
      setEnviando(false);
    }
  };

  const opcionesRol = [
    {
      valor: 'tecnico',
      titulo: 'Técnico',
      descripcion: 'Gestiona tus avisos asignados',
      icono: (
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a4 4 0 1 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="m14 7 3 3" />
        </svg>
      ),
    },
    {
      valor: 'administrador',
      titulo: 'Administrador',
      descripcion: 'Acceso total y métricas',
      icono: (
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3 4 6v5c0 4.5 3.2 8.2 8 10 4.8-1.8 8-5.5 8-10V6l-8-3Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="m9.5 12 1.8 1.8L15 10" />
        </svg>
      ),
    },
  ];

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-background px-4 py-10">
      {/* Resplandores decorativos de fondo */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-gray-900/80 p-8 shadow-2xl shadow-black/40 backdrop-blur-sm">
          {/* Encabezado con icono */}
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 ring-1 ring-brand-orange/30">
            <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-brand-orange" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 8v6M22 11h-6" />
            </svg>
          </div>
          <h3 className="mt-4 text-center text-2xl font-bold tracking-tight text-white">Solicitar acceso</h3>
          <p className="mt-1 text-center text-sm leading-relaxed text-gray-400">
            Rellena tus datos y un administrador revisará tu solicitud.
            Recibirás acceso en cuanto sea aprobada.
          </p>

          {mensaje && (
            <div
              role="alert"
              className={`mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                tipoMensaje === 'exito'
                  ? 'border-emerald-800 bg-emerald-950/40 text-emerald-200'
                  : 'border-red-800 bg-red-950/40 text-red-200'
              }`}
            >
              <svg viewBox="0 0 24 24" fill="none" className="mt-0.5 h-4 w-4 flex-shrink-0" stroke="currentColor" strokeWidth="2">
                {tipoMensaje === 'exito' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.86 1.8 18a1 1 0 0 0 .86 1.5h18.68a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.7 0Z" />
                )}
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          <form onSubmit={manejarEnvio} className="mt-6 space-y-4">
            {/* Nombre */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="nombre">
                Nombre completo
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="12" cy="8" r="4" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 21c0-3.9 3.6-7 8-7s8 3.1 8 7" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Ej. María López"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                  id="nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="email">
                Correo electrónico
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4 7 8 6 8-6" />
                  </svg>
                </span>
                <input
                  type="email"
                  placeholder="tucorreo@ejemplo.com"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="password">
                Contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                    <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
                  </svg>
                </span>
                <input
                  type={mostrarPassword ? 'text' : 'password'}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarPassword((valor) => !valor)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition hover:text-brand-orange"
                  aria-label={mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {mostrarPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.4 5.5A10.4 10.4 0 0 1 12 5c5 0 9 4 10 7-.4 1.1-1.1 2.3-2.1 3.4M6.6 6.6C4.6 8 3.4 9.8 3 12c1 3 5 7 9 7 1.3 0 2.5-.3 3.6-.9" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Selector de rol tipo tarjeta */}
            <div>
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400">
                Tipo de cuenta solicitada
              </span>
              <div className="grid grid-cols-2 gap-3">
                {opcionesRol.map((opcion) => {
                  const seleccionado = rol === opcion.valor;
                  return (
                    <button
                      key={opcion.valor}
                      type="button"
                      onClick={() => setRol(opcion.valor)}
                      className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition ${
                        seleccionado
                          ? 'border-brand-orange bg-brand-orange/10 text-white shadow-inner'
                          : 'border-gray-700 bg-gray-800/60 text-gray-300 hover:border-gray-600 hover:bg-gray-800'
                      }`}
                      aria-pressed={seleccionado}
                    >
                      <span className={`flex items-center gap-1.5 text-sm font-semibold ${seleccionado ? 'text-brand-orange' : 'text-gray-200'}`}>
                        {opcion.icono}
                        {opcion.titulo}
                      </span>
                      <span className="text-xs text-gray-400">{opcion.descripcion}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={enviando}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviando && (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
                </svg>
              )}
              {enviando ? 'Enviando solicitud...' : 'Solicitar acceso'}
            </button>

            <div className="pt-1 text-center text-sm text-gray-400">
              ¿Ya tienes cuenta?{' '}
              <Link to="/iniciar-sesion" className="font-medium text-brand-orange hover:underline">
                Inicia sesión
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SolicitarAcceso;
